// Single source of truth for the password rules the signup / set-password /
// new-password-challenge screens enforce.
//
// This mirrors the PROD user pool (ap-south-1_gOkKMbFNz): 12 characters with
// upper, lower, digit and symbol. It deliberately does NOT vary by environment.
//
// Staging (ap-south-1_SFPwNJ7W5) is looser at 8 characters and no symbol, and
// three separate copies of this rule used to mirror *staging*. The result was
// that a prod user could satisfy every on-screen criterion, submit, and have
// Cognito reject the password with InvalidPasswordException -- leaving them with
// no account and a later login that reported only "Incorrect email or password".
//
// A single strict rule is correct against both pools, because Cognito rejects
// passwords that are too weak, never ones that are too strong. If the prod
// policy changes, change it here and nowhere else.
export const PASSWORD_MIN_LENGTH = 12;

// Cognito's accepted symbol set, verbatim. A looser test such as /[^A-Za-z0-9]/
// would show a green tick for characters Cognito rejects (accented letters, for
// example), which is the same false-pass this module exists to prevent.
const SYMBOL = /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+= ]/;

export type PasswordCriterion = { label: string; met: boolean };

export const passwordCriteria = (password: string): PasswordCriterion[] => [
  {
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    met: password.length >= PASSWORD_MIN_LENGTH,
  },
  { label: "One uppercase letter", met: /[A-Z]/.test(password) },
  { label: "One lowercase letter", met: /[a-z]/.test(password) },
  { label: "One number", met: /[0-9]/.test(password) },
  { label: "One symbol", met: SYMBOL.test(password) },
];

/** Returns the first unmet rule as user-facing copy, or null when valid. */
export const validatePasswordStrength = (pw: string): string | null => {
  if (pw.length < PASSWORD_MIN_LENGTH)
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (!/[a-z]/.test(pw)) return "Password must include a lowercase letter.";
  if (!/[A-Z]/.test(pw)) return "Password must include an uppercase letter.";
  if (!/[0-9]/.test(pw)) return "Password must include a number.";
  if (!SYMBOL.test(pw)) return "Password must include a symbol.";
  return null;
};

export const meetsPasswordPolicy = (pw: string): boolean =>
  validatePasswordStrength(pw) === null;
